import { useEffect, useState } from 'react';
import OneliveSidebar from './OneliveSidebar';
import OneliveShell from './OneliveShell';
import { LiveRoom, fetchLiveRoom, fetchLiveRooms, navigate } from './api';
import { guardLiveRoomPageAccess } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';

interface LiveRoomPageProps {
  roomUuid?: string;
}

const MOCK_CHAT = [
  { user: '팬1', msg: '안녕하세요~' },
  { user: '팬2', msg: '오늘도 화이팅!' },
  { user: '팬3', msg: '❤️❤️❤️' },
];

export default function LiveRoomPage({ roomUuid }: LiveRoomPageProps) {
  const liveUiMessages = useLiveUiMessages();
  const [room, setRoom] = useState<LiveRoom | null>(null);
  const [sidebarRooms, setSidebarRooms] = useState<LiveRoom[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void guardLiveRoomPageAccess(liveUiMessages);
  }, [liveUiMessages]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [r, list] = await Promise.all([
          roomUuid ? fetchLiveRoom(roomUuid) : Promise.resolve(null),
          fetchLiveRooms(12),
        ]);
        if (mounted) {
          setRoom(r);
          setSidebarRooms(list);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [roomUuid]);

  if (loading) {
    return <div className="onelive-page"><div className="onelive-main onelive-loading">방송 로딩 중...</div></div>;
  }

  if (!room) {
    return (
      <div className="onelive-page">
        <div className="onelive-main onelive-empty">
          <p>방송을 찾을 수 없습니다.</p>
          <button type="button" className="onelive-btn-primary" onClick={() => navigate('/live')}>목록으로</button>
        </div>
      </div>
    );
  }

  return (
    <OneliveShell>
    <div className="onelive-page onelive-room-page">
      <OneliveSidebar rooms={sidebarRooms} activeUuid={room.uuid} />
      <main className="onelive-main onelive-room-layout">
        <div className="onelive-room-player-wrap">
          <div className="onelive-room-player">
            {room.stream_url ? (
              <video src={room.stream_url} controls autoPlay playsInline className="onelive-video" />
            ) : (
              <div className="onelive-player-placeholder">
                <span className="onelive-player-play">▶</span>
                <p>LIVE</p>
              </div>
            )}
          </div>
          <div className="onelive-room-meta">
            <div className="onelive-room-meta-left">
              <div className="onelive-room-avatar">
                {room.avatar ? <img src={room.avatar} alt="" /> : room.bj_nickname?.[0]}
              </div>
              <div>
                <h1>{room.bj_nickname}</h1>
                <p>{room.title}</p>
              </div>
            </div>
            <div className="onelive-room-meta-right">
              <span className="onelive-live-badge">LIVE</span>
              <span>{room.viewer_count ?? 0}명 시청 중</span>
            </div>
          </div>
          {room.description && <p className="onelive-room-desc">{room.description}</p>}
        </div>

        <aside className="onelive-room-chat">
          <div className="onelive-chat-header">실시간 채팅</div>
          <div className="onelive-chat-messages">
            {MOCK_CHAT.map((c, i) => (
              <div key={i} className="onelive-chat-msg">
                <strong>{c.user}</strong> {c.msg}
              </div>
            ))}
          </div>
          <form
            className="onelive-chat-input"
            onSubmit={(e) => { e.preventDefault(); setChatInput(''); }}
          >
            <input
              type="text"
              placeholder="메시지를 입력하세요"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
            />
            <button type="submit">전송</button>
          </form>
        </aside>
      </main>
    </div>
    </OneliveShell>
  );
}
